import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '@kostbar/i18n'
import {
  buildInventoryColumns,
  isDerivedRow,
} from '../components/inventory-columns'
import { InventoryMovementModal } from '../components/inventory-movement-modal'
import { StockActionDialog } from '../components/stock-action-dialog'
import { stockInfoFromFields } from '../hooks/use-stock-info'
import type { StockActionKind } from '../components/stock-action-dialog'
import type { InventoryViewItem } from '@/api/endpoints/inventory-view'
import type { StockKind } from '@/api/types'
import type { Sort } from '@/components/data-table/types'
import { inventoryViewApi } from '@/api/endpoints/inventory-view'
import { categoriesApi } from '@/api/endpoints/categories'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { DataTable } from '@/components/data-table/data-table'
import { RowActions } from '@/components/data-table/row-actions'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

const INVENTORY_QUERY_KEY = 'inventory-page'

/** The kinds the list filters by (CHG-2026-09-stock-usage-and-price-twins, task 11.1). */
const STOCK_KINDS: Array<StockKind> = [
  'article',
  'dish',
  'variant',
  'not_tracked',
]

export function InventoryPage() {
  const { t: tInventory } = useTranslation('inventory')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const { data: restaurantsData } = useRestaurants()
  const { can } = usePermissions()
  const canReceive = can(ACTIONS.INVENTORY_RECEIVE)
  const canCount = can(ACTIONS.INVENTORY_COUNT)
  const canAdjust = can(ACTIONS.INVENTORY_ADJUST)
  const hasRowActions = canReceive || canAdjust
  const restaurants = restaurantsData?.items ?? []

  const [restaurantId, setRestaurantId] = useState<number | null>(null)
  const [movementOpen, setMovementOpen] = useState(false)
  const [stockAction, setStockAction] = useState<{
    item: InventoryViewItem
    kind: StockActionKind
  } | null>(null)

  useEffect(() => {
    if (!restaurantId && restaurants.length > 0) {
      setRestaurantId(restaurants[0].id)
    }
  }, [restaurants, restaurantId])

  const { data: cats } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  })

  const columns = useMemo(
    () => buildInventoryColumns(tInventory, tCommon),
    [tInventory, tCommon],
  )

  const stockKindOptions = useMemo(
    () =>
      [
        { label: tInventory('filters.stockKindOptions.all'), value: 'all' },
      ].concat(
        STOCK_KINDS.map((kind) => ({
          label: tInventory(`filters.stockKindOptions.${kind}`),
          value: kind,
        })),
      ),
    [tInventory],
  )

  const categoryOptions = useMemo(
    () =>
      [{ label: tInventory('filters.categoryAll'), value: 'all' }].concat(
        (cats?.items ?? []).map((c) => ({ label: c.name, value: c.code })),
      ),
    [cats, tInventory],
  )

  async function fetchInventory({
    pageIndex,
    pageSize,
    sorting,
    filters,
    q,
  }: {
    pageIndex: number
    pageSize: number
    sorting?: Array<Sort>
    filters?: Record<string, unknown>
    q?: string
  }) {
    if (!restaurantId) return { rows: [], total: 0, pageCount: 0 }

    const categoryCode =
      typeof filters?.categoryCode === 'string' &&
      filters.categoryCode !== 'all'
        ? filters.categoryCode
        : undefined
    const onlyOnMenu = filters?.onlyOnMenu === true
    const onlyInPosLayout = filters?.onlyInPosLayout === true
    const onlyRecentMovements = filters?.onlyRecentMovements === true
    const onlyIncoming = filters?.onlyIncoming === true
    const includeUnassigned = filters?.includeUnassigned === true
    const stockKind = STOCK_KINDS.find((kind) => kind === filters?.stockKind)

    const res = await inventoryViewApi.list({
      restaurantId,
      search: q || undefined,
      categoryCode,
      onlyOnMenu,
      onlyInPosLayout,
      onlyRecentMovements,
      onlyIncoming,
      includeUnassigned,
      stockKind,
      sortBy: sorting?.[0]?.id,
      sortDir: sorting?.[0]?.id
        ? sorting[0].desc
          ? 'desc'
          : 'asc'
        : undefined,
      page: pageIndex,
      pageSize,
    })
    return {
      rows: res.items,
      total: res.total,
      pageCount: Math.ceil(res.total / res.pageSize),
    }
  }

  return (
    <PermissionGate action={ACTIONS.INVENTORY_VIEW}>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Select
              value={restaurantId != null ? String(restaurantId) : undefined}
              onValueChange={(v) => setRestaurantId(Number(v))}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {restaurants.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/inventory/history">
                {tInventory('actions.history')}
              </Link>
            </Button>
            <Button
              onClick={() => setMovementOpen(true)}
              disabled={!canReceive && !canCount}
            >
              {tInventory('actions.newMovement')}
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            {!restaurantId ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {tCommon('loading')}
              </div>
            ) : (
              <DataTable<InventoryViewItem>
                queryKey={`${INVENTORY_QUERY_KEY}-${restaurantId}`}
                columns={columns}
                fetcher={fetchInventory}
                filters={[
                  {
                    id: 'categoryCode',
                    label: tInventory('filters.category'),
                    type: 'select',
                    options: categoryOptions,
                  },
                  {
                    id: 'stockKind',
                    label: tInventory('filters.stockKind'),
                    type: 'select',
                    options: stockKindOptions,
                  },
                  {
                    id: 'onlyOnMenu',
                    label: tInventory('filters.onlyOnMenu'),
                    type: 'boolean',
                  },
                  {
                    id: 'onlyInPosLayout',
                    label: tInventory('filters.onlyInPosLayout'),
                    type: 'boolean',
                  },
                  {
                    id: 'onlyRecentMovements',
                    label: tInventory('filters.onlyRecentMovements'),
                    type: 'boolean',
                  },
                  {
                    id: 'onlyIncoming',
                    label: tInventory('filters.onlyIncoming'),
                    type: 'boolean',
                  },
                  {
                    id: 'includeUnassigned',
                    label: tInventory('filters.includeUnassigned'),
                    type: 'boolean',
                  },
                ]}
                globalSearch={{
                  placeholder: tInventory('page.searchPlaceholder'),
                }}
                selectable={false}
                enableColumnVisibility
                showDensityToggle={false}
                initialState={{ pageSize: 50 }}
                getRowId={(row) => row.sku}
                rowActions={
                  hasRowActions
                    ? (row) => (
                        <RowActions>
                          {/*
                            A dish, variant or not-tracked article holds no stock of its own: the
                            server refuses every write (product_has_no_stock), so none is offered.
                          */}
                          {isDerivedRow(row.original) && (
                            <DropdownMenuItem
                              disabled
                              className="max-w-64 whitespace-normal text-xs"
                            >
                              {tInventory('noStock.actionsHint')}
                            </DropdownMenuItem>
                          )}
                          {canReceive && !isDerivedRow(row.original) && (
                            <DropdownMenuItem
                              onClick={() =>
                                setStockAction({
                                  item: row.original,
                                  kind: 'receive',
                                })
                              }
                            >
                              {tInventory('actions.receive')}
                            </DropdownMenuItem>
                          )}
                          {canAdjust && !isDerivedRow(row.original) && (
                            <>
                              <DropdownMenuItem
                                onClick={() =>
                                  setStockAction({
                                    item: row.original,
                                    kind: 'setOnHand',
                                  })
                                }
                              >
                                {tInventory('actions.setOnHand')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  setStockAction({
                                    item: row.original,
                                    kind: 'setBuffer',
                                  })
                                }
                              >
                                {tInventory('actions.setBuffer')}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link
                              to="/products/$sku"
                              params={{ sku: row.original.sku }}
                            >
                              {tCommon('view')}
                            </Link>
                          </DropdownMenuItem>
                        </RowActions>
                      )
                    : undefined
                }
              />
            )}
            <p className="mt-4 text-sm text-muted-foreground">
              {tInventory('page.footnote')}
            </p>
          </CardContent>
        </Card>

        <InventoryMovementModal
          open={movementOpen}
          onClose={() => setMovementOpen(false)}
          defaultRestaurantId={restaurantId ?? undefined}
        />

        {stockAction && restaurantId && (
          <StockActionDialog
            open
            onOpenChange={(open) => {
              if (!open) setStockAction(null)
            }}
            action={stockAction.kind}
            sku={stockAction.item.sku}
            name={stockAction.item.name}
            restaurantId={restaurantId}
            // The row carries counting mode and unit (phase B4): no extra lookup for the dialog.
            stock={stockInfoFromFields(stockAction.item) ?? undefined}
            onSuccess={() =>
              queryClient.invalidateQueries({
                queryKey: [`${INVENTORY_QUERY_KEY}-${restaurantId}`],
              })
            }
          />
        )}
      </div>
    </PermissionGate>
  )
}
